import { useQuery } from "@apollo/client"
import { useState } from "react"
import invariant from "tiny-invariant"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { gql } from "~/__generated__"
import {
  AdminUserEntryFragment,
  AgencyRoleEnum,
  BusinessRelationshipEnum,
  BusinessTypeEnum,
} from "~/__generated__/graphql"
import { useSafeMutation } from "~/common/useSafeMutation"
import { PublicMediaKitSwitch } from "~/components/public-media-kit/PublicMediaKitSwitch"
import { displayErrors } from "~/common/validations"
import { Button } from "~/shadcn/ui/button"
import { Label } from "~/shadcn/ui/label"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/shadcn/ui/form"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/shadcn/ui/select"
import { Switch } from "~/ui/Switch"
import { ADMIN_UPDATE_USER_MUTATION } from "../api"

const formSchema = z.object({
  agencyRole: z.string().optional(),
  agencyId: z.string().optional(),
  businessRelationship: z.string().optional(),
  foundingAgency: z.boolean().optional(),
})

type FormValues = z.infer<typeof formSchema>

type AdminSettingsModalProps = {
  user: AdminUserEntryFragment
  onClose: () => void
  onUserUpdated: () => void
}

export const agenciesQuery = gql(/* GraphQL */ `
  query AdminAgencies {
    agencies(first: 1000) {
      edges {
        node {
          id
          name
        }
      }
    }
  }
`)

export const AdminSettingsModal = ({
  user,
  onClose,
  onUserUpdated,
}: AdminSettingsModalProps) => {
  const [updateUser] = useSafeMutation(ADMIN_UPDATE_USER_MUTATION)
  const agenciesQueryResult = useQuery(agenciesQuery, {
    fetchPolicy: "cache-first",
  })

  const agencies =
    agenciesQueryResult.data?.agencies.edges.map((edge) => edge.node) || []

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      agencyRole: user.agencyRole || undefined,
      agencyId: user.agency?.id || undefined,
      businessRelationship: user.agency?.businessRelationship || undefined,
      foundingAgency: user.agency?.foundingAgency ?? false,
    },
  })

  // The founding-agency flag lives on the agency, edited via the nested `agency`
  // attributes on userUpdate — which target the user's CURRENT agency. If the admin
  // is reassigning the user to a different agency in this same save, sending the flag
  // would apply it to the old agency, so we disable + omit it during reassignment.
  const watchedAgencyId = form.watch("agencyId")
  const isReassigningAgency = watchedAgencyId !== user.agency?.id

  const onSubmit = async (values: FormValues) => {
    const result = await updateUser({
      variables: {
        input: {
          userId: user.id,
          agencyRole: values.agencyRole as AgencyRoleEnum,
          agencyId: values.agencyId,
          businessRelationship:
            values.businessRelationship as BusinessRelationshipEnum,
          // Only send the founding-agency flag when it targets the user's existing
          // agency (see isReassigningAgency above). userUpdate resolves the nested
          // agency id from the user's current agency.
          ...(user.agency && !isReassigningAgency
            ? { agency: { foundingAgency: values.foundingAgency ?? false } }
            : {}),
        },
      },
    })

    if (result.errors) {
      displayErrors(result.errors)
      return
    }

    onUserUpdated()
    onClose()
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Admin Settings</h2>
        <p className="text-sm text-gray-500">
          Manage agency assignment and role for {user.email}
        </p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <FormField
            control={form.control}
            name="agencyId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Agency</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select agency" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {agencies.map((agency) => (
                      <SelectItem key={agency.id} value={agency.id}>
                        {agency.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="agencyRole"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Agency Role</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select role" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={AgencyRoleEnum.AgencyAdmin}>
                      Agency Admin
                    </SelectItem>
                    <SelectItem value={AgencyRoleEnum.Agent}>Agent</SelectItem>
                    <SelectItem value={AgencyRoleEnum.Intern}>
                      Intern
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="businessRelationship"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Business Relationship</FormLabel>
                <Select
                  onValueChange={field.onChange}
                  defaultValue={field.value}
                >
                  <FormControl>
                    <SelectTrigger>
                      <SelectValue placeholder="Select business relationship" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value={BusinessRelationshipEnum.Free}>
                      Free
                    </SelectItem>
                    <SelectItem value={BusinessRelationshipEnum.Standard}>
                      Standard
                    </SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>
            )}
          />

          {user.agency && (
            <FormField
              control={form.control}
              name="foundingAgency"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Founding Agency</FormLabel>
                  <div className="flex flex-row items-center justify-between gap-4">
                    <p className="text-sm text-gray-500">
                      Show the Founding Agency badge on this agency across the
                      app. Affects the whole agency, not just this user.
                    </p>
                    <Switch
                      aria-label="Founding Agency"
                      enabled={field.value ?? false}
                      setEnabled={(toggle) => field.onChange(toggle)}
                      disabled={isReassigningAgency}
                    />
                  </div>
                  {isReassigningAgency && (
                    <p className="text-xs text-gray-400">
                      Save the new agency assignment and reopen settings to edit
                      its Founding Agency badge.
                    </p>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          )}

          {user.agency?.businessType === BusinessTypeEnum.Talent && (
            <AdminPublicMediaKitRow
              agency={user.agency}
              onChanged={onUserUpdated}
            />
          )}

          <div className="flex justify-end gap-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Save Changes</Button>
          </div>
        </form>
      </Form>
    </div>
  )
}

// The agency's public media kit, switched by a site admin (ZAN-902). It saves
// on its own, not with Save Changes, and always targets the agency the user
// belonged to when the modal opened.
const AdminPublicMediaKitRow = ({
  agency,
  onChanged,
}: {
  agency: NonNullable<AdminUserEntryFragment["agency"]>
  onChanged: () => void
}) => {
  invariant(
    agency.publicMediaKitEnabled != null,
    "Admins can read every agency's publication setting"
  )
  const [enabled, setEnabled] = useState(agency.publicMediaKitEnabled)

  return (
    <div className="space-y-2">
      <Label htmlFor="admin.publicMediaKitEnabled" className="text-gray50">
        Public Media Kit
      </Label>
      <div className="flex flex-row items-center justify-between gap-4">
        <p className="text-sm text-gray-500">
          Lets anyone with the link see {agency.name}'s roster and media kits.
          Saves immediately and affects the whole agency.
        </p>
        <PublicMediaKitSwitch
          id="admin.publicMediaKitEnabled"
          agencyId={agency.id}
          variant={{ kind: "admin", agencyName: agency.name }}
          enabled={enabled}
          onChanged={(value) => {
            setEnabled(value)
            onChanged()
          }}
        />
      </div>
    </div>
  )
}
